import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	forwardRef,
	HostBinding,
	Injector,
	Input,
	OnInit,
	inject,
	input,
	InputSignal,
	DestroyRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';

@Component({
	selector: 'time-picker-input',
	templateUrl: './time-picker.component.html',
	styleUrls: ['./time-picker.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TimePickerComponent),
			multi: true
		}
	],
	standalone: false
})
export class TimePickerComponent implements ControlValueAccessor, OnInit, AfterViewInit {
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	private injector = inject(Injector);
	private cdRef = inject(ChangeDetectorRef);

	@HostBinding('attr.data-test-id') get dataTestId(): string {
		return this.ngControl?.name?.toString();
	}

	readonly placeholder: InputSignal<string> = input<string>(undefined);
	@Input() label: string;
	@Input() hint: string;

	public readonly HOURS_FORMAT: number = 24;

	public lawyalTheme: NgxMaterialTimepickerTheme = {
		dial: {
			dialBackgroundColor: '#fff',
			dialActiveColor: '#607bee',
			dialInactiveColor: '#9aa4d0'
		},
		clockFace: {
			clockHandColor: '#607bee'
		}
	};
	public timePattern: RegExp = new RegExp('^(?:[01]\\d|2[0-3]):[0-5]\\d$');
	public selectedTime: string;
	public errorType: string;
	public isRequired: boolean;
	public isDisabled: boolean;
	public ngControl: NgControl;
	public onChange: any = () => {};
	public onTouch: any = () => {};

	ngOnInit(): void {
		this.ngControl = this.injector.get(NgControl);
	}

	ngAfterViewInit(): void {
		this.subscribeToControlChanges();
		this.subscribeToStatusChanges();
		this.setRequiredStatus();
		this.cdRef.detectChanges();
	}

	private setRequiredStatus(): void {
		this.isRequired = !!(this.ngControl.control?.errors && this.ngControl.control?.errors.hasOwnProperty('required'));
	}

	private getErrorType(): void {
		if (!this.ngControl.control?.errors) {
			this.errorType = null;
		} else if (this.ngControl?.control?.touched) {
			if (this.ngControl.control?.hasError('required')) {
				this.errorType = 'VALIDATORS.REQUIRED';
			}
			if (this.ngControl.control?.hasError('invalidTime')) {
				this.errorType = 'VALIDATORS.START_END_BAD_RANGE';
			}
			if (this.ngControl.control?.hasError('badFormat')) {
				this.errorType = 'VALIDATORS.BAD_FORMAT';
			}
		}
	}

	private subscribeToStatusChanges(): void {
		this.ngControl.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.getErrorType();
		});
	}

	private subscribeToControlChanges(): void {
		this.ngControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.setRequiredStatus();
			this.getErrorType();
			this.cdRef.detectChanges();
		});
	}

	private createTimeString(value: string): string {
		if (value) {
			return `${value.slice(0, 2)}:${
				value.includes(':') ? value.slice(value.lastIndexOf(':') + 1, value.length) : value.slice(2, value.length)
			}`;
		}
	}

	public writeValue(value: any): void {
		this.selectedTime = value;
		this.cdRef.detectChanges();
	}

	public registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouch = fn;
	}

	public setTouchState(isTouched: boolean): void {
		this.onTouch(isTouched);
	}

	public setDisabledState?(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
		this.cdRef.detectChanges();
	}

	public setSelectedTime(time: string): void {
		this.selectedTime = time;
		this.onTouch(this.selectedTime);
		this.onChange(this.selectedTime);
	}

	public onValueChange(event: Event): void {
		const input: HTMLInputElement = event.target as HTMLInputElement;
		const value: string = input.value && input.value !== '' ? input.value : null;
		this.onTouch(value);

		if (!value) {
			this.onChange(value);
			return;
		}

		if (this.timePattern.test(value)) {
			this.selectedTime = value;
			this.onChange(value);
		} else {
			this.ngControl.control.setErrors({ badFormat: true });
		}
	}

	public onInputChange(event: Event): void {
		const input: HTMLInputElement = event.target as HTMLInputElement;
		const value: string = this.createTimeString(input.value);

		if (this.timePattern.test(value)) {
			this.selectedTime = value;
		}
	}
}
